import { useMemo, useState } from 'react';
import styles from './CompetitorViewer.module.css';

const toDomainKey = (value) => {
  if (!value) return '';
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0];
};

const openUrlSafely = (url) => {
  if (!url) return;
  try {
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch {
    // best effort only
  }
};

const extractSocialLinks = (contactInfo) => {
  if (!contactInfo || typeof contactInfo !== 'object') return [];
  const entries = [];
  const addLinks = (label, value) => {
    if (!value) return;
    const list = Array.isArray(value) ? value : [value];
    list
      .map((item) => (item ? String(item).trim() : ''))
      .filter(Boolean)
      .forEach((url) => {
        if (!entries.find((entry) => entry.url === url)) {
          entries.push({ label, url });
        }
      });
  };
  addLinks('Facebook', contactInfo.facebook);
  addLinks('Instagram', contactInfo.instagram);
  addLinks('TikTok', contactInfo.tiktok);
  return entries;
};

const Section = ({ title, children, action }) => (
  <div className={styles.section}>
    <div className={styles.sectionHeader}>
      <h4 className={styles.sectionTitle}>{title}</h4>
      {action ? <div className={styles.sectionAction}>{action}</div> : null}
    </div>
    {children}
  </div>
);

const normalizeImageUrl = (url) => {
  if (!url) return '';
  const trimmed = String(url).trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('data:image')) return '';
  if (trimmed.startsWith('blob:')) return '';
  return trimmed;
};

const collectImageTilesByCompetitor = (competitors) =>
  (competitors || [])
    .map((competitor) => {
      const images = Array.isArray(competitor?.image_urls)
        ? competitor.image_urls
        : [];
      const tiles = images
        .map(normalizeImageUrl)
        .filter(Boolean)
        .map((imageUrl, idx) => ({
          id: `${competitor?.domain || competitor?.name || 'competitor'}-${idx}`,
          imageUrl,
          title: competitor?.name || competitor?.domain || 'Competitor',
          subtitle: competitor?.domain || '',
          domain: competitor?.domain || null,
        }));
      return {
        competitor,
        tiles,
      };
    })
    .filter((group) => group.tiles.length);

const CompetitorViewer = ({ data = {}, pricingReport = null, onGeneratePricing = null, pricingLoading = false }) => {
  const payload = data?.data || data;
  const summary =
    typeof payload?.summary === 'string' ? payload.summary.trim() : '';
  const abcProducts = Array.isArray(payload?.abc_products)
    ? payload.abc_products
    : [];
  const aiAnalysis = payload?.ai_analysis || {};
  const competitiveLandscape = aiAnalysis?.competitive_landscape || {};
  const digitalVisibility =
    competitiveLandscape?.digital_visibility_demand_capture || {};
  const directCompetitors = Array.isArray(payload?.direct_competitors)
    ? payload.direct_competitors
    : [];

  const groupTabs = [
    { key: 'direct', label: 'Direct' },
    { key: 'indirect', label: 'Indirect' },
    { key: 'adjacent', label: 'Adjacent' },
  ];
  const [activeGroup, setActiveGroup] = useState('direct');

  const activeLandscapeSummary = competitiveLandscape?.[`${activeGroup}_summary`];
  const activeLandscapeItems = Array.isArray(
    competitiveLandscape?.[`${activeGroup}_competitors`]
  )
    ? competitiveLandscape[`${activeGroup}_competitors`]
    : [];
  const activeDirectCards = activeGroup === 'direct' ? directCompetitors : [];
  const activeImageGroups = useMemo(
    () => collectImageTilesByCompetitor(activeLandscapeItems),
    [activeLandscapeItems]
  );
  const activeImageGroupMap = useMemo(() => {
    const map = new Map();
    activeImageGroups.forEach((group) => {
      const key = toDomainKey(
        group?.competitor?.domain || group?.competitor?.name || ''
      );
      if (key) map.set(key, group);
    });
    return map;
  }, [activeImageGroups]);

  const imageryTiles = useMemo(() => {
    const sitemap = Array.isArray(payload?.sitemap_contents)
      ? payload.sitemap_contents
      : [];
    const tiles = [];
    sitemap.forEach((source) => {
      const productSitemap = Array.isArray(source?.product_sitemap)
        ? source.product_sitemap
        : [];
      productSitemap.forEach((entry) => {
        const image = Array.isArray(entry?.images) ? entry.images[0] : null;
        if (!image?.loc) return;
        tiles.push({
          id: `${source?.host || 'host'}-${entry?.loc || 'loc'}-${image.loc}`,
          imageUrl: image.loc,
          productUrl: entry?.loc,
          title:
            image.title ||
            entry?.image_title ||
            entry?.loc?.replace(/^https?:\/\//, '') ||
            source?.host ||
            'Product',
          host: source?.host,
        });
      });
    });
    return tiles;
  }, [payload?.sitemap_contents]);

  const renderLandscapeGroup = (title, summary, items) => {
    const list = Array.isArray(items) ? items : [];
    if (!list.length && !summary) return null;
    return (
      <Section title={title}>
        {summary ? <p className={styles.sectionSummary}>{summary}</p> : null}
        {list.map((competitor, index) => (
          <div
            key={competitor.domain || competitor.name || `${title}-${index}`}
            className={styles.competitorRow}
          >
            <div className={styles.analysisCard}>
              <h5 className={styles.analysisTitle}>
                {competitor.name || competitor.domain || 'Unknown competitor'}
              </h5>
              {competitor.domain ? (
                <span className={styles.domainText}>{competitor.domain}</span>
              ) : null}
              {competitor.market_positioning ? (
                <p className={styles.analysisText}>{competitor.market_positioning}</p>
              ) : null}
              <div className={styles.analysisMetaRow}>
                {competitor.pricing_strategy ? (
                  <div className={styles.analysisMetaBlock}>
                    <span className={styles.analysisMetaLabel}>Pricing</span>
                    <span className={styles.analysisMetaValue}>
                      {competitor.pricing_strategy}
                    </span>
                  </div>
                ) : null}
                {Number.isFinite(competitor.sku_count) ? (
                  <div className={styles.analysisMetaBlock}>
                    <span className={styles.analysisMetaLabel}>SKU Count</span>
                    <span className={styles.analysisMetaValue}>
                      {competitor.sku_count}
                    </span>
                  </div>
                ) : null}
                {competitor.traffic_estimate ? (
                  <div className={styles.analysisMetaBlock}>
                    <span className={styles.analysisMetaLabel}>Estimate Traffic</span>
                    <span className={styles.analysisMetaValue}>
                      {competitor.traffic_estimate}
                    </span>
                  </div>
                ) : null}
              </div>
              {competitor.conversion_approach ? (
                <p className={styles.analysisText}>
                  Homepage Summary: {competitor.conversion_approach}
                </p>
              ) : null}
              {competitor.trust_signals ? (
                <p className={styles.analysisText}>
                  Trust: {competitor.trust_signals}
                </p>
              ) : null}
              {competitor?.tech_stack_summary ? (
                <p className={styles.analysisText}>
                  Tech stack: {competitor.tech_stack_summary}
                </p>
              ) : null}
              {(() => {
                const seo = competitor?.seo_keyword_overlap;
                if (!seo) return null;
                return (
                  <p className={styles.analysisText}>
                    Demand capture: {seo.total_matches ?? '—'} matches,{' '}
                    {seo.unique_keywords ?? '—'} keywords
                    {seo.summary ? `. ${seo.summary}` : ''}
                  </p>
                );
              })()}
              {competitor?.competitor_domain_analysis?.content_summary ? (
                <p className={styles.analysisText}>
                  Content: {competitor.competitor_domain_analysis.content_summary}
                </p>
              ) : null}
              {/* {competitor?.price_position_analysis ? (
                <p className={styles.analysisText}>
                  Price position: {competitor.price_position_analysis}
                </p>
              ) : null} */}
              {(() => {
                const socialLinks = extractSocialLinks(competitor?.contact_info);
                if (!socialLinks.length) return null;
                return (
                  <div className={styles.socialRow}>
                    {socialLinks.map((link, idx) => (
                      <button
                        key={`${link.label}-${idx}`}
                        className={styles.socialButton}
                        onClick={() => openUrlSafely(link.url)}
                      >
                        <span className={styles.socialText}>{link.label}</span>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Homepage imagery block removed */}
          </div>
        ))}
      </Section>
    );
  };

  if (
    !abcProducts.length &&
    !directCompetitors.length &&
    !imageryTiles.length &&
    !competitiveLandscape?.direct_competitors?.length &&
    !competitiveLandscape?.indirect_competitors?.length &&
    !competitiveLandscape?.adjacent_competitors?.length &&
    !digitalVisibility?.summary
  ) {
    return (
      <Section title="Competitor Snapshot">
        <p className={styles.emptyText}>No competitor insights available.</p>
      </Section>
    );
  }

  return (
    <div className={styles.shell}>
      <div className={styles.container}>
        {summary ? (
          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>Report Summary</p>
            <p className={styles.summaryText}>{summary}</p>
          </div>
        ) : null}
        {/* Tabs */}
        <div className={styles.icpTabs}>
          {groupTabs.map((tab) => {
            const active = tab.key === activeGroup;
            return (
              <button
                key={tab.key}
                className={`${styles.icpTab} ${active ? styles.icpTabActive : ''}`}
                onClick={() => setActiveGroup(tab.key)}
              >
                <span className={`${styles.icpTabText} ${active ? styles.icpTabTextActive : ''}`}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Direct Competitors Cards */}
        {activeDirectCards.length ? (
          <Section title="Direct Competitors">
            {activeDirectCards.map((competitor, index) => (
              <div
                key={competitor.host || `direct-${index}`}
                className={styles.competitorCard}
              >
                <div className={styles.competitorHeader}>
                  <h5 className={styles.competitorHost}>
                    {competitor.host || 'Unknown host'}
                  </h5>
                  {competitor.source ? (
                    <span className={styles.sourceBadge}>
                      <span className={styles.sourceBadgeText}>
                        {competitor.source}
                      </span>
                    </span>
                  ) : null}
                </div>
                {competitor.domain ? (
                  <span className={styles.domainText}>{competitor.domain}</span>
                ) : null}
                {competitor.why_relevant ? (
                  <p className={styles.competitorWhy}>
                    {competitor.why_relevant}
                  </p>
                ) : null}
                <div className={styles.competitorStats}>
                  <div className={styles.competitorStat}>
                    <span className={styles.metricLabel}>Matches</span>
                    <span className={styles.metricValue}>
                      {competitor.product_match_count ?? '—'}
                    </span>
                  </div>
                  <div className={styles.competitorStat}>
                    <span className={styles.metricLabel}>Hits</span>
                    <span className={styles.metricValue}>
                      {competitor.hits ?? '—'}
                    </span>
                  </div>
                  <div className={styles.competitorStat}>
                    <span className={styles.metricLabel}>Country</span>
                    <span className={styles.metricValue}>
                      {competitor.same_country ? 'Local' : 'International'}
                    </span>
                  </div>
                </div>
                {Array.isArray(competitor.sample_links) &&
                competitor.sample_links.length ? (
                  <button
                    className={styles.competitorLink}
                    onClick={() => openUrlSafely(competitor.sample_links[0])}
                  >
                    View sample product
                  </button>
                ) : null}
              </div>
            ))}
          </Section>
        ) : null}

        {/* Competitive Landscape */}
        <div className={styles.landscapeGrid}>
          {renderLandscapeGroup(
            `Competitive Landscape: ${activeGroup.replace(/^\w/, (c) => c.toUpperCase())}`,
            activeLandscapeSummary,
            activeLandscapeItems
          )}
        </div>

        {/* Competitor Imagery */}
        {/*
        {imageryTiles.length ? (
          <Section title="Competitor Imagery">
            <p className={styles.bentoIntro}>
              Tap any tile to open the live product page.
            </p>
            <div className={styles.bentoGrid}>
              {imageryTiles.map((tile, index) => {
                const isWide = index % 5 === 0;
                return (
                  <div
                    key={tile.id}
                    className={`${styles.bentoCard} ${isWide ? styles.bentoCardWide : styles.bentoCardTall}`}
                    onClick={() => openUrlSafely(tile.productUrl)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && openUrlSafely(tile.productUrl)}
                  >
                    {tile.imageUrl ? (
                      <img
                        src={tile.imageUrl}
                        alt={tile.title}
                        className={styles.bentoImage}
                      />
                    ) : null}
                    <div className={styles.bentoOverlay}>
                      <p className={styles.bentoTitle}>{tile.title}</p>
                      <p className={styles.bentoSubtitle}>{tile.host}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        ) : null}
        */}

        {/*
        <Section
          title="Competitor Pricing"
          action={
            typeof onGeneratePricing === 'function' ? (
              <button
                className={styles.generatePricingBtn}
                onClick={onGeneratePricing}
                disabled={pricingLoading}
              >
                {pricingLoading ? 'Generating...' : pricing ? 'Refresh' : 'Generate pricing analysis'}
              </button>
            ) : null
          }
        >
          {!pricing && !pricingLoading && (
            <p className={styles.emptyText}>
              No competitor pricing analysis yet. Click "Generate pricing analysis" to search the web and compare market prices against your competitors.
            </p>
          )}
          {pricingLoading && (
            <p className={styles.emptyText}>Searching the web and generating pricing comparison...</p>
          )}
          {pricing && (
            <div className={styles.pricingContainer}>
              {pricing.market_price_range?.summary && (
                <p className={styles.sectionSummary}>{pricing.market_price_range.summary}</p>
              )}
              {pricing.our_price_position_summary && (
                <p className={styles.analysisText}>{pricing.our_price_position_summary}</p>
              )}
              {pricing.online_market_context && (
                <p className={styles.analysisText}>{pricing.online_market_context}</p>
              )}
              {pricing.pricing_opportunity && (
                <div className={styles.pricingOpportunity}>
                  <span className={styles.analysisMetaLabel}>Opportunity</span>
                  <p className={styles.analysisText}>{pricing.pricing_opportunity}</p>
                </div>
              )}
              {Array.isArray(pricing.competitor_pricing) && pricing.competitor_pricing.length > 0 && (
                <div className={styles.pricingCompetitorList}>
                  {pricing.competitor_pricing.map((c, i) => (
                    <div key={c.name || i} className={styles.analysisCard}>
                      <h5 className={styles.analysisTitle}>{c.name || 'Competitor'}</h5>
                      {c.domain && <span className={styles.domainText}>{c.domain}</span>}
                      <div className={styles.analysisMetaRow}>
                        {c.estimated_price_range && (
                          <div className={styles.analysisMetaBlock}>
                            <span className={styles.analysisMetaLabel}>Price range</span>
                            <span className={styles.analysisMetaValue}>{c.estimated_price_range}</span>
                          </div>
                        )}
                        {c.pricing_approach && (
                          <div className={styles.analysisMetaBlock}>
                            <span className={styles.analysisMetaLabel}>Approach</span>
                            <span className={styles.analysisMetaValue}>{c.pricing_approach}</span>
                          </div>
                        )}
                      </div>
                      {c.key_difference && (
                        <p className={styles.analysisText}>{c.key_difference}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Section>
        */}
      </div>
    </div>
  );
};

export default CompetitorViewer;
